'use client';

import { motion } from 'motion/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { useLocale } from '@/contexts/LocaleContext';
import { BlogTag, CasesTag } from '@/payload-types';
import { getDictionary } from '@/utils/getDictionary';

import styles from './Filters.module.scss';

type Props = {
  allFilters: BlogTag[] | CasesTag[];
};

export const Filters: React.FC<Props> = ({ allFilters }) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const t = getDictionary(locale);
  const selectedTag = searchParams.get('tag') || '';
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const clearFilters = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('tag');
    router.push(`?${params.toString()}`, { scroll: false });
  };

  const toggleFilter = (tag: BlogTag) => {
    const params = new URLSearchParams(searchParams.toString());
    const tagIdString = String(tag.id);

    if (selectedTag === tagIdString) {
      params.delete('tag');
    } else {
      params.set('tag', tagIdString);
    }

    router.push(`?${params.toString()}`, { scroll: false });
  };

  const updateFades = () => {
    const el = scrollerRef.current;
    if (!el) return;

    const { clientWidth, scrollLeft, scrollWidth } = el;
    const atStart = scrollLeft <= 0;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1; // tolerance

    setShowLeftFade(!atStart);
    setShowRightFade(!atEnd);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    updateFades(); // initial check

    el.addEventListener('scroll', updateFades);
    window.addEventListener('resize', updateFades);

    return () => {
      el.removeEventListener('scroll', updateFades);
      window.removeEventListener('resize', updateFades);
    };
  }, []);

  const fadeClasses = [
    styles.filtersOverflow,
    showLeftFade ? styles.showLeft : '',
    showRightFade ? styles.showRight : '',
  ].join(' ');

  return (
    <div className={styles.wrapper}>
      <span className={styles.label}>{t('filters.label')}</span>
      <div className={fadeClasses}>
        <div className={styles.filters} ref={scrollerRef}>
          <button
            onClick={clearFilters}
            className={`${styles.filterButton} ${!selectedTag ? styles.active : ''}`}
          >
            <span className={styles.filterButtonLabel}>{t('filters.all')}</span>
            {!selectedTag && (
              <motion.div
                className={styles.filterButtonBlob}
                layoutId='filterButtonEffect'
              />
            )}
          </button>

          {allFilters.map((tag) => {
            const isSelected = selectedTag === String(tag.id);

            return (
              <button
                key={tag.id}
                onClick={() => toggleFilter(tag)}
                className={`${styles.filterButton} ${isSelected ? styles.active : ''}`}
              >
                <span className={styles.filterButtonLabel}>{tag.title} </span>
                {isSelected && (
                  <motion.div
                    className={styles.filterButtonBlob}
                    layoutId='filterButtonEffect'
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
